"use client";

import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageOldAnalytics } from "@/context/AnalyticsProvider/gamePageOld";
import { cn } from "@/lib/utils";
import { Gamepad2 } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import ReactPlayer from "react-player-v3";
import { CarouselIndicator } from "../Carouselndicator";
import { GameplayModal } from "./GameplayModal";

interface GameplayCarouselProps {
  gameData: GameData;
  className?: string;
}

export const GameplayCarousel = ({
  gameData,
  className,
}: GameplayCarouselProps) => {
  const { track } = useGamePageOldAnalytics();
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev ===
        (gameData.gameContent?.gameplay_gallery_thumbnail_urls?.length ?? 0)
          ? 1
          : prev + 1,
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [gameData.gameContent?.gameplay_gallery_thumbnail_urls?.length]);

  const handleOpenModal = useCallback(() => {
    if (gameData.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-open",
          control: "Gameplay",
          controlType: ControlType.BUTTON,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
    setIsModalOpen(true);
  }, [gameData.details.game_id, gameData.details.name, track]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") handleOpenModal();
    },
    [handleOpenModal],
  );

  return (
    <>
      <GameplayModal
        gameData={gameData}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <div
        className={cn(
          "relative glass-container-border cursor-pointer",
          className,
        )}
        onClick={handleOpenModal}
        onKeyDown={handleKeyDown}
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
      >
        {/* Grid layout to stack images on top of each other */}
        <div className="absolute inset-0 grid grid-cols-1 grid-rows-1">
          {gameData.gameContent?.gameplay_gallery_thumbnail_urls?.map(
            (url, index) =>
              // ONLY WEBP IS SUPPORTED TO ENSURE LOWER SIZES, CONVERT IF NEEDED
              url.endsWith(".webp") ? (
                <Image
                  key={url}
                  src={url}
                  alt={`Gameplay ${index + 1}`}
                  priority={index === currentIndex - 1}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  className={cn(
                    "col-start-1 row-start-1 object-cover rounded-[calc(1.25rem-1px)] transition-all duration-200",
                    index === currentIndex - 1
                      ? "opacity-100"
                      : "opacity-0 hidden",
                  )}
                />
              ) : (
                <ReactPlayer
                  key={url}
                  src={url}
                  playing={index === currentIndex - 1}
                  preload="metadata"
                  playsInline
                  muted
                  loop
                  width="100%"
                  height="100%"
                  className={cn(
                    "overflow-hidden size-full col-start-1 row-start-1 [--media-object-fit:cover] rounded-[calc(1.25rem-1px)] transition-all duration-200",
                    index === currentIndex - 1
                      ? "opacity-100"
                      : "opacity-0 hidden",
                  )}
                />
              ),
          )}
        </div>

        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        <div className="absolute left-[10%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <Gamepad2 className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Gameplay</p>
        </div>

        <CarouselIndicator
          currentIndex={currentIndex}
          total={
            gameData.gameContent?.gameplay_gallery_thumbnail_urls?.length ?? 0
          }
          className="absolute bottom-5 right-5"
        />
      </div>
    </>
  );
};
